import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@mui/material';
import { Box } from '@mui/system';
import { alpha, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PublicOffIcon from '@mui/icons-material/PublicOff';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PublicIcon from '@mui/icons-material/Public';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import {
  AUTHOR_COLORS, diffItemAgainstPublic, getAuthorName, getDomain, getDomainFromUrl,
  getHeatBuzz, getImageInfo, getPrimaryLink, getPrimaryLinkUrl, getSiteImageUrl, mergeSite,
  mergePublicAndPrivateItems, shareActionTitle, shareChangesState, shareResultMessage,
} from '../../common/ItemTools';
import { DELETE_LABEL, UNSHARE_LABEL } from '../../common/ActionLabels';
import { isItemHidden, VISIBILITY_HIDDEN, VISIBILITY_VISIBLE } from '../../common/Visibility';
import { hasFlag, withoutFlag } from '../../common/UrlFlags';
import {
  bookmarkItem, deleteItem, enhanceUnfetchedItemUrls, fetchItemSuggestions, fetchItemsByIds, shareItem,
  updateItem,
} from '../../redux/features/items/itemsSlice';
import { createAuthor } from '../../redux/features/authors/authorsSlice';
import {
  createTag, selectAllTagsPublic, selectResolveShareTags, selectTagVocabulary,
} from '../../redux/features/tags/tagsSlice';
import { removeItemFromList, updateList } from '../../redux/features/lists/listsSlice';
import { setSnackbar } from '../../redux/features/snackbarSlice';
import { getNamespacePath } from '../../redux/features/context/contextSlice';
import useConfirm from '../../hooks/useConfirm';
import useGoBack from '../../hooks/useGoBack';
import useImageSize from '../../hooks/useImageSize';
import ItemCard from '../ItemCard';
import ListInfoCard from '../ListInfoCard';
import AddItemToListDialog from '../AddItemToListDialog';
import useAuthorHoverCard from '../AuthorHoverCard';
import useSiteHoverCard from '../SiteHoverCard';
import AuthorAvatar from './AuthorAvatar';
import CoverCandidateValue from './CoverCandidateValue';
import ItemHeroEditForm from './EditForm';
import ItemSourceData from './SourceData';
import FavoriteButton from '../common/hero/FavoriteButton';
import ShareButton from '../common/hero/ShareButton';
import CompletenessScore from '../common/CompletenessScore';
import HeatBuzzBadge from '../common/HeatBuzzBadge';
import {
  EDIT_FONT_SIZE, ERROR, HAIRLINE, MUTED, SANS_STACK, SERIF_STACK, SUCCESS, TEXT_BODY, TEXT_DARK, TEXT_SECONDARY,
} from '../common/tokens';
import {
  AccentButton, ActionDivider, BackButton, DangerButton, PageBackground, HeroCard,
  TextInput, TextArea, SectionLabel, GroupLabel, OutlinedButton, PrimaryButton,
  TagChip, ChipRemove, IconToggle, CardCornerButton, CardDeleteButton, Pill,
  autocompleteSx, cardGridSx, cardCellSx, cardInnerSx,
} from '../common/hero/styles';
import {
  withCreateOption, optionLabel, formatDate, formatReviewed,
  dateTimeToInput, inputDateTimeToUnix, timestampToInput, inputToTimestamp,
  normalizeTag, openUrlInNewTab, followableUrl,
} from '../common/hero/utils';
import HeroVerdictActions from '../common/hero/VerdictActions';
import useVerdict from '../common/hero/useVerdict';
import renderTagOption, { tagPopperSlotProps } from '../common/TagOption';
import SuggestionPanel, {
  SUGGEST_ICON_SIZE, SuggestionAgrees, SuggestionLabel, SuggestionSkeleton, SuggestionSpinner,
} from '../common/hero/SuggestionPanel';
import {
  typeColor, typeLabel, languageName, ctaVerb, itemUrlEntries, parseReadTime,
  publishedIsDateOnly, combineFetchedItem, buildDraft, draftCompleteness,
  suggestionPatch, restoreSlice, authorCandidateProfile, authorProfileForCreate,
  hasUnfetchedUrls, lookUpResultMessage,
} from './utils';
import useItemExtras from './useItemExtras';

export default function ItemHeroPage(props) {
  const {
    item: rawItem, listInfos, namespace, onRefetch,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirm, confirmDialog } = useConfirm();
  const userInfo = useSelector((state) => state.users.userInfo);
  const isModerator = Boolean(userInfo?.isModerator);
  const vocabulary = useSelector(selectTagVocabulary);
  const publicTags = useSelector(selectAllTagsPublic);

  // Pending edits are written to the pending namespace rather than private
  const isPending = namespace === 'pending';
  const editNamespace = isPending ? 'pending' : 'private';
  // Offer public tags only for pending item edits by moderators
  const userTags = isPending ? publicTags : vocabulary;

  const theme = useTheme();
  const { main: coral, dark: coralDark } = theme.palette.primary;

  const item = useMemo(() => mergePublicAndPrivateItems(rawItem), [rawItem]);
  const { decide, deciding } = useVerdict('item', rawItem.id, item.title);
  const resolveShareTags = useSelector(selectResolveShareTags);
  const shareDiff = useMemo(
    () => diffItemAgainstPublic(rawItem, resolveShareTags(rawItem?.privateData?.tagIds)),
    [rawItem, resolveShareTags],
  );

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [newTag, setNewTag] = useState('');
  const [newRelated, setNewRelated] = useState('');
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const sourceData = useItemExtras(rawItem.id, namespace);
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [applied, setApplied] = useState({});
  const [restorable, setRestorable] = useState({});
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [relatedCache, setRelatedCache] = useState({});
  const fetchedRef = useRef(new Set());

  const namespacePath = getNamespacePath(namespace);

  const relatedIds = useMemo(
    () => (editing && draft
      ? draft.related.map((r) => r.id)
      : (item.relatedItems || []).map((r) => r.id)),
    [editing, draft, item],
  );

  useEffect(() => {
    const missing = relatedIds.filter((id) => id && !fetchedRef.current.has(id));
    if (!missing.length) return;
    missing.forEach((id) => fetchedRef.current.add(id));
    dispatch(fetchItemsByIds({ namespace, ids: missing }))
      .unwrap()
      .then((responses) => {
        const additions = {};
        responses.forEach((resp) => {
          const merged = combineFetchedItem(resp);
          if (merged) additions[merged.id] = merged;
        });
        if (Object.keys(additions).length) {
          setRelatedCache((prev) => ({ ...prev, ...additions }));
        }
      })
      .catch(() => { /* missing related items are simply skipped */ });
  }, [relatedIds, namespace, dispatch]);

  const relatedCards = relatedIds.map((id) => relatedCache[id]).filter(Boolean);

  const clearSuggestions = () => {
    setSuggestions(null);
    setSuggesting(false);
    setApplied({});
    setRestorable({});
  };

  const enterEdit = () => {
    setDraft(buildDraft(item));
    setNewTag('');
    setNewRelated('');
    clearSuggestions();
    setEditing(true);
    if (isModerator) sourceData.load();
  };
  const cancelEdit = () => {
    setDraft(null);
    clearSuggestions();
    setEditing(false);
  };

  // Auto-enter edit mode when arriving with the ?edit flag. The flag is consumed
  // once so a later refresh or back-navigation doesn't force editing again.
  useEffect(() => {
    if (!hasFlag(searchParams, 'edit')) return;
    setSearchParams(withoutFlag(searchParams, 'edit'), { replace: true });
    if (userInfo && !editing) enterEdit();
  }, [searchParams, userInfo]);

  const setField = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  const toggleDateOnly = () => setDraft((d) => {
    const dateOnly = !d.dateOnly;
    const date = (d.datePublished || '').slice(0, 10);
    const datePublished = date && !dateOnly ? `${date}T00:00` : date;
    return { ...d, dateOnly, datePublished };
  });

  const saveEdit = async () => {
    setSaving(true);
    try {
      // Resolve tags (create any without an id), preserving order
      const tagIds = [];
      // eslint-disable-next-line no-restricted-syntax -- sequential await, order matters
      for (const t of draft.tags) {
        if (t.id) {
          tagIds.push(t.id);
        } else if (t.name && !isPending) {
          // eslint-disable-next-line no-await-in-loop
          const created = await dispatch(createTag({ tag: { name: t.name } })).unwrap();
          tagIds.push(created.id);
        }
      }
      // Resolve authors (create any without an id).
      const authorIds = [];
      // eslint-disable-next-line no-restricted-syntax -- sequential await, order matters
      for (const a of draft.authors) {
        if (a.authorId) {
          authorIds.push(a.authorId);
        } else if (a.name.trim() && !isPending) {
          // eslint-disable-next-line no-await-in-loop
          const created = await dispatch(createAuthor({
            author: { profiles: [authorProfileForCreate(a)] },
          })).unwrap();
          authorIds.push(created.id);
        }
      }

      const patch = {};
      if (draft.title !== (item.title || '')) patch.title = draft.title;
      if (draft.excerpt !== (item.excerpt || '')) patch.excerpt = draft.excerpt;
      if (draft.coverImageUrl !== (item.coverImageUrl || '')) patch.coverImageUrl = draft.coverImageUrl;
      if (Number(draft.type) !== (item.type || 0)) patch.type = Number(draft.type);
      if (draft.lang !== (item.lang || '')) patch.lang = draft.lang;

      const targetReadTime = parseReadTime(draft.readTime);
      if (targetReadTime !== (item.timeNeeded || 0)) patch.timeNeeded = targetReadTime;

      // Compare each date against its original input string (at the same
      // granularity the field edits).
      const origDateOnly = publishedIsDateOnly(item);
      const origPublished = timestampToInput(item.timePublished, origDateOnly);
      if (draft.datePublished !== origPublished || Boolean(draft.dateOnly) !== Boolean(origDateOnly)) {
        const targetPublished = inputToTimestamp(draft.datePublished, draft.dateOnly);
        if (targetPublished) {
          patch.timePublished = targetPublished;
          patch.timePublishedDateOnly = Boolean(draft.dateOnly);
        }
      }

      if (draft.dateCreated !== dateTimeToInput(item.timeCreated)) {
        const targetCreated = inputDateTimeToUnix(draft.dateCreated);
        if (targetCreated) patch.timeCreated = targetCreated;
      }

      if (draft.dateReviewed !== dateTimeToInput(item.timeReviewed)) {
        patch.timeReviewed = inputDateTimeToUnix(draft.dateReviewed);
      }

      const origTagIds = (item.tags || []).map((t) => t.id);
      if (JSON.stringify(tagIds) !== JSON.stringify(origTagIds)) patch.tagIds = tagIds;

      const origAuthorIds = (item.authors || []).map((a) => a.id);
      if (JSON.stringify(authorIds) !== JSON.stringify(origAuthorIds)) patch.authorIds = authorIds;

      const draftUrls = draft.urls
        .map((u) => ({ url: followableUrl(u.url), visible: u.visible }))
        .filter((u) => u.url);
      if (JSON.stringify(draftUrls) !== JSON.stringify(itemUrlEntries(item))) {
        patch.links = draftUrls.map((u, i) => ({
          url: u.url,
          // The primary link is always visible; visibility toggles the rest.
          ...(i === 0 ? { primary: true } : { visible: u.visible }),
        }));
      }

      const draftRelated = draft.related.map((r) => r.id);
      const origRelated = (item.relatedItems || []).map((r) => r.id);
      if (JSON.stringify(draftRelated) !== JSON.stringify(origRelated)) {
        patch.relatedItems = draftRelated.map((id) => ({ id }));
      }

      const reviewed = Boolean(suggestions);
      if (Object.keys(patch).length || reviewed) {
        await dispatch(updateItem({
          namespace: editNamespace, itemId: rawItem.id, patch, reviewed,
        })).unwrap();
      }
      setEditing(false);
      setDraft(null);
    } catch (error) {
      dispatch(setSnackbar({
        snackbarOpen: true,
        snackbarType: 'error',
        snackbarMessage: 'Failed to save changes.',
      }));
    } finally {
      setSaving(false);
    }
  };

  // Add a free-typed tag name: normalize, dedupe, and reuse an existing tag
  // entity from the vocabulary when the name matches (else create on save).
  // While moderating unmatched names are dropped.
  const addTagName = (raw) => {
    const name = normalizeTag(raw);
    if (!name || draft.tags.some((t) => t.name === name)) return;
    const existing = userTags.find((t) => t.name === name);
    if (!existing && isPending) return;
    setField('tags', [...draft.tags, existing ? { id: existing.id, name } : { id: null, name }]);
  };
  // Handle an Autocomplete selection (existing tag object, "Add x" option, or
  // free-typed string).
  const addTagValue = (value) => {
    if (!value) return;
    if (typeof value === 'string') {
      addTagName(value);
    } else if (value.inputValue) {
      addTagName(value.inputValue);
    } else if (value.id && !draft.tags.some((t) => t.id === value.id)) {
      setField('tags', [...draft.tags, { id: value.id, name: value.name }]);
    }
    setNewTag('');
  };
  const removeTag = (name) => setField('tags', draft.tags.filter((t) => t.name !== name));

  const tagOptions = userTags.filter((t) => !draft?.tags.some((dt) => dt.id === t.id));

  const addRelated = () => {
    const id = newRelated.trim().replace(/^#/, '');
    if (!id) return;
    if (draft.related.some((r) => r.id === id)) { setNewRelated(''); return; }
    setField('related', [...draft.related, { id }]);
    setNewRelated('');
  };
  const removeRelated = (id) => setField('related', draft.related.filter((r) => r.id !== id));

  // Add an author from the autocomplete: an existing suggestion ({ authorId,
  // name }) or a free-typed name (authorId null → created on save). Deduped by
  // id or, for new names, case-insensitively.
  const addAuthorEntry = ({
    authorId, name, suggestId, profile,
  }) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    const dup = draft.authors.some((a) => (
      (authorId && a.authorId === authorId)
      || (!authorId && !a.authorId && a.name.trim().toLowerCase() === trimmed.toLowerCase())
    ));
    if (dup) return;
    setField('authors', [
      ...draft.authors,
      {
        authorId: authorId || null,
        name: trimmed,
        color: AUTHOR_COLORS[draft.authors.length % AUTHOR_COLORS.length],
        ...(profile && { profile }),
        ...(profile?.imageUrl && { imageUrl: profile.imageUrl }),
        // Set only when a suggestion created the entry.
        ...(suggestId && { suggestId }),
      },
    ]);
  };
  const setAuthorName = (idx, value) => setField('authors', draft.authors.map(
    (a, i) => (i === idx ? { ...a, name: value } : a),
  ));
  const removeAuthor = (idx) => setField('authors', draft.authors.filter((_, i) => i !== idx));
  // Saved as the order of `authorIds`, which is the byline's order.
  const moveAuthor = (from, to) => setDraft((d) => {
    const authors = [...d.authors];
    const [moved] = authors.splice(from, 1);
    authors.splice(to, 0, moved);
    return { ...d, authors };
  });

  const addUrl = () => setField('urls', [...draft.urls, { url: '', visible: true }]);
  const setUrl = (idx, value) => setField('urls', draft.urls.map((u, i) => (i === idx ? { ...u, url: value } : u)));
  const removeUrl = (idx) => setField('urls', draft.urls.filter((_, i) => i !== idx));
  // Promote a URL to primary by moving it to the front; index 0 is always the
  // primary link. Other URLs keep their relative order.
  const makeUrlPrimary = (idx) => setField('urls', [
    draft.urls[idx],
    ...draft.urls.filter((_, i) => i !== idx),
  ]);
  const toggleUrlVisible = (idx) => setField('urls', draft.urls.map(
    (u, i) => (i === idx ? { ...u, visible: !u.visible } : u),
  ));

  const reportError = (message) => dispatch(setSnackbar({
    snackbarOpen: true, snackbarType: 'error', snackbarMessage: message,
  }));

  // Fetch what the item's URLs never had fetched, so Suggest has it to offer.
  // Takes the saved links, as Suggest does, not the draft's.
  const runLookUp = () => {
    if (lookingUp) return;
    setLookingUp(true);
    dispatch(enhanceUnfetchedItemUrls({ namespace: editNamespace, itemId: rawItem.id }))
      .unwrap()
      .then((result) => dispatch(setSnackbar({
        snackbarOpen: true,
        snackbarType: 'success',
        snackbarMessage: lookUpResultMessage(result),
      })))
      .catch(() => reportError('Failed to look up the item\'s pages.'))
      // Even a failed call may have fetched some first. The spinner holds until
      // the reload says whether any are left, so the button doesn't flash back.
      .then(() => sourceData.load())
      .finally(() => setLookingUp(false));
  };

  // Fill the panels from what the item's URLs already know.
  const runSuggest = () => {
    if (suggesting) return;
    setSuggesting(true);
    dispatch(fetchItemSuggestions({ namespace: editNamespace, itemId: rawItem.id }))
      .unwrap()
      .then((result) => setSuggestions(result))
      .catch(() => reportError('Failed to load suggestions.'))
      .finally(() => setSuggesting(false));
  };

  const candidatesFor = (field) => suggestions?.fields?.[field]?.candidates || [];
  const agreesOn = (field) => Boolean(suggestions?.fields?.[field]?.agrees);

  const restoreField = (field) => {
    if (restorable[field]) setDraft((d) => ({ ...d, ...restorable[field] }));
    setApplied((a) => { const next = { ...a }; delete next[field]; return next; });
    setRestorable((r) => { const next = { ...r }; delete next[field]; return next; });
  };

  // Write a candidate into the draft, or take it back out when it's the one
  // already applied. The first apply remembers what the field held, so
  // restoring returns the user's own value rather than the item's.
  const applyCandidate = (field, candidate) => {
    if (applied[field] === candidate.id) {
      restoreField(field);
      return;
    }
    const patch = suggestionPatch(field, candidate.value);
    if (!applied[field]) {
      setRestorable((r) => ({ ...r, [field]: restoreSlice(draft, patch) }));
    }
    setDraft((d) => ({ ...d, ...patch }));
    setApplied((a) => ({ ...a, [field]: candidate.id }));
  };

  // Authors are multi-select: a candidate adds an author rather than replacing
  // a value, and is tracked on the draft entry it created.
  const toggleAuthorCandidate = (candidate) => {
    if (draft.authors.some((a) => a.suggestId === candidate.id)) {
      setField('authors', draft.authors.filter((a) => a.suggestId !== candidate.id));
      return;
    }
    const profile = authorCandidateProfile(candidate);
    addAuthorEntry({
      authorId: candidate.authorId,
      name: profile.name || profile.username,
      suggestId: candidate.id,
      ...(!candidate.authorId && { profile }),
    });
  };

  const toggleFavorite = () => {
    dispatch(updateItem({ namespace: 'private', itemId: rawItem.id, patch: { isFavorite: !item.isFavorite } }))
      .unwrap()
      .catch(() => reportError('Failed to favorite item.'));
  };
  const toggleHidden = () => {
    const patch = { visibility: isItemHidden(item) ? VISIBILITY_VISIBLE : VISIBILITY_HIDDEN };
    dispatch(updateItem({ namespace: 'private', itemId: rawItem.id, patch }))
      .unwrap()
      .catch(() => reportError('Failed to update item visibility.'));
  };
  const toggleReviewed = () => {
    const patch = { timeReviewed: item.timeReviewed ? null : Math.round(Date.now() / 1000) };
    dispatch(updateItem({ namespace: 'private', itemId: rawItem.id, patch }))
      .unwrap()
      .catch(() => reportError('Failed to update reviewed status.'));
  };
  const toggleBookmark = () => {
    if (item.hasPrivateData) return;
    dispatch(bookmarkItem({ itemId: rawItem.id }))
      .unwrap()
      .then(() => onRefetch && onRefetch())
      .catch(() => reportError('Failed to bookmark item.'));
  };
  const share = () => {
    dispatch(shareItem({ itemId: rawItem.id }))
      .unwrap()
      .then((result) => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: shareResultMessage(result),
        }));
        if (onRefetch) onRefetch();
      })
      .catch(() => reportError('Failed to share the item.'));
  };

  const removeItem = async () => {
    const ok = await confirm({
      title: 'Delete item?',
      message: `"${item.title || 'This item'}" will be permanently removed from your library.`,
      confirmLabel: DELETE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteItem({ namespace: 'private', itemId: rawItem.id }))
      .unwrap()
      .then(goBack)
      .catch(() => reportError('Failed to delete item.'));
  };

  const unshareItem = async () => {
    const ok = await confirm({
      title: 'Unshare item?',
      message: `"${item.title || 'This item'}" will be removed from the public record. Its creator keeps their private copy.`,
      confirmLabel: UNSHARE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteItem({ namespace: 'public', itemId: rawItem.id }))
      .unwrap()
      .then(() => {
        if (item.hasPrivateData) {
          if (onRefetch) onRefetch();
        } else {
          goBack();
        }
      })
      .catch(() => reportError('Failed to unshare item.'));
  };

  const removeListMembership = (listId) => {
    dispatch(removeItemFromList({ namespace: 'private', listId, itemId: rawItem.id }))
      .unwrap()
      .then(() => onRefetch && onRefetch())
      .catch(() => reportError('Failed to remove from list.'));
  };

  const setListCover = (listId) => {
    const patch = { coverImageUrl: draft.coverImageUrl.trim() };
    dispatch(updateList({ namespace: 'private', listId, patch }))
      .unwrap()
      .then(() => onRefetch && onRefetch())
      .catch(() => reportError('Failed to set the list cover.'));
  };

  const itemCardActions = {
    onItemRead: (it) => navigate(`/items/${it.id}`),
    onItemOpen: (it) => openUrlInNewTab(getPrimaryLinkUrl(it)),
    onTagOpen: (tagId) => navigate(`/${namespacePath}/items?tag=${tagId}`),
    onAuthorView: (authorId) => navigate(`/authors/${authorId}`),
    onAuthorFilter: (authorId) => navigate(`/${namespacePath}/items?author=${authorId}`),
    onSiteView: (siteId) => navigate(`/sites/${siteId}`),
    onSiteFilter: (siteId) => navigate(`/${namespacePath}/items?site=${siteId}`),
  };
  const onListOpen = useCallback((listInfo) => {
    const merged = mergePublicAndPrivateItems(listInfo) || listInfo;
    navigate(`/lists/${merged.id}`);
  }, [navigate]);

  const authors = item.authors || [];
  const namedAuthors = authors.filter((a) => getAuthorName(a));
  const domain = getDomain(item) || 'source';
  const bylineDate = formatDate(
    item.timePublished || item.timeCreated,
    item.timePublished ? item.timePublishedDateOnly : false,
  );
  const coverInfo = getImageInfo(item);
  const coverUrl = editing && draft ? draft.coverImageUrl : coverInfo.url;
  const coverSize = useImageSize(editing ? coverUrl : null);
  const draftCoverUrl = (draft?.coverImageUrl || '').trim();
  const primaryLink = getPrimaryLink(item);
  const primaryUrl = primaryLink ? primaryLink.url : null;
  const primarySiteId = primaryLink ? primaryLink.siteId : null;
  const primarySite = (primaryLink && primaryLink.site)
    || (domain && domain !== 'source' ? { domain } : null);
  const primarySiteIcon = (primaryLink && primaryLink.site && getSiteImageUrl(mergeSite(primaryLink.site)))
    || (domain && domain !== 'source' && getSiteImageUrl({ domain }));
  const secondaryLinks = useMemo(() => {
    const seen = new Set(primaryUrl ? [primaryUrl] : []);
    const out = [];
    (item.links || []).forEach((l) => {
      if (!l.primary && l.visible && l.url && !seen.has(l.url)) {
        seen.add(l.url);
        const linkDomain = getDomainFromUrl(l.url);
        const site = l.site ? mergeSite(l.site) : null;
        const icon = (site && getSiteImageUrl(site))
          || (linkDomain && getSiteImageUrl({ domain: linkDomain }));
        out.push({ url: l.url, domain: linkDomain, icon });
      }
    });
    return out;
  }, [item, primaryUrl]);
  const isReviewed = Boolean(item.timeReviewed);
  const isHidden = isItemHidden(item);
  const hasPublicData = Boolean(rawItem.publicData);
  const shareChanges = shareChangesState(shareDiff);
  const shareTitle = shareActionTitle(hasPublicData, shareDiff);
  const showTracking = Boolean(userInfo) && !isPending;
  const showRelated = (editing && !isPending) || relatedIds.length > 0;
  const showLists = (editing && !isPending) || (listInfos && listInfos.length > 0);
  const editEntities = editing && !isPending;
  const canDelete = !isPending && item.hasPrivateData;
  const canUnshare = !isPending && hasPublicData && isModerator;
  const canDecide = isPending && isModerator;
  const showSourceData = isModerator;
  const showLookUp = isModerator && (lookingUp || hasUnfetchedUrls(rawItem, sourceData.extras));
  const readTimeMinutes = item.timeNeeded;
  const { heatScore, buzzScore } = getHeatBuzz(item);
  const completeness = draft ? draftCompleteness(draft) : null;

  return (
    <PageBackground sx={{ padding: 0 }}>
      <HeroCard>
        {/* Toolbar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', padding: '16px 28px 0', maxWidth: 1232, mx: 'auto',
        }}>
          <BackButton type="button" onClick={goBack}>
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
            <span>Back</span>
          </BackButton>
          <Box sx={{ flex: 1 }} />
          {!editing && userInfo && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {canDelete && (
                <DangerButton type="button" onClick={removeItem}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  {DELETE_LABEL}
                </DangerButton>
              )}
              {canUnshare && (
                <DangerButton type="button" onClick={unshareItem}>
                  <PublicOffIcon sx={{ fontSize: 16 }} />
                  {UNSHARE_LABEL}
                </DangerButton>
              )}
              <AccentButton type="button" onClick={enterEdit}>
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
                Edit
              </AccentButton>
              {canDecide && (
                <>
                  <ActionDivider />
                  <HeroVerdictActions onDecide={decide} disabled={deciding} />
                </>
              )}
            </Box>
          )}
          {editing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {completeness && (
                <CompletenessScore score={completeness.score} missing={completeness.missing} />
              )}
              {showLookUp && (
                <AccentButton
                  type="button"
                  onClick={runLookUp}
                  disabled={lookingUp || saving}
                  title="Fetch metadata for this item's pages that were never fetched, AI's included — a paid call"
                >
                  {lookingUp
                    ? <SuggestionSpinner />
                    : <TravelExploreIcon sx={{ fontSize: SUGGEST_ICON_SIZE }} />}
                  <SuggestionLabel busy={lookingUp} label="Look up" busyLabel="Looking up…" />
                </AccentButton>
              )}
              {/* Waits for Look up, which would leave its candidates stale. */}
              <AccentButton type="button" onClick={runSuggest} disabled={suggesting || lookingUp || saving}>
                {suggesting
                  ? <SuggestionSpinner />
                  : <AutoAwesomeIcon sx={{ fontSize: SUGGEST_ICON_SIZE }} />}
                <SuggestionLabel busy={suggesting} />
              </AccentButton>
              <OutlinedButton type="button" onClick={cancelEdit} disabled={saving}>Cancel</OutlinedButton>
              <PrimaryButton type="button" onClick={saveEdit} disabled={saving}>
                <CheckIcon sx={{ fontSize: 16 }} />
                Save
              </PrimaryButton>
            </Box>
          )}
        </Box>

        {/* Cover */}
        <Box sx={{ maxWidth: 1232, mx: 'auto', mt: '18px', px: '28px' }}>
          <Box
            component={!editing && primaryUrl ? 'a' : 'div'}
            href={!editing && primaryUrl ? primaryUrl : undefined}
            target={!editing && primaryUrl ? '_blank' : undefined}
            rel={!editing && primaryUrl ? 'noopener noreferrer' : undefined}
            sx={{
              position: 'relative',
              width: '100%',
              height: 360,
              borderRadius: '2px',
              overflow: 'hidden',
              background: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: !editing && primaryUrl ? 'pointer' : 'default',
            }}
          >
            {coverUrl && (
              <Box
                component="img"
                src={coverUrl}
                alt={item.title || ''}
                sx={{
                  width: '100%', height: '100%', objectFit: 'contain', display: 'block',
                }}
              />
            )}
            <HeatBuzzBadge heat={heatScore} buzz={buzzScore} />
          </Box>
          {editing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '8px' }}>
              <Box sx={{
                fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, whiteSpace: 'nowrap',
              }}>
                Cover URL
                {agreesOn('coverImageUrl') && <SuggestionAgrees />}
              </Box>
              {/* Fixed width, so the URL field doesn't shift while a new cover is measured. */}
              <Box sx={{
                minWidth: '11ch', fontSize: 11, color: MUTED, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
              }}>
                {coverSize && `${coverSize.width} × ${coverSize.height}`}
              </Box>
              <TextInput
                value={draft.coverImageUrl}
                onChange={(e) => setField('coverImageUrl', e.target.value)}
                placeholder="Paste an image URL"
                style={{ flex: 1 }}
              />
            </Box>
          )}
          {editing && suggesting && <SuggestionSkeleton height={56} />}
          {editing && (
            <SuggestionPanel
              candidates={candidatesFor('coverImageUrl')}
              appliedId={applied.coverImageUrl}
              onPick={(c) => applyCandidate('coverImageUrl', c)}
              onRestore={() => restoreField('coverImageUrl')}
              renderValue={(c) => <CoverCandidateValue candidate={c} />}
              sx={{ mt: '10px' }}
            />
          )}
        </Box>

        {/* Body */}
        <Box sx={{ maxWidth: 1064, mx: 'auto', padding: '24px 28px 24px' }}>
          {/* Header block */}
          <Box sx={{ mb: '20px' }}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'flex-start' },
              gap: '10px',
            }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {!editing && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <HeroSiteIcon
                      src={primarySiteIcon}
                      site={primarySite}
                      siteId={primarySiteId}
                      onSiteView={itemCardActions.onSiteView}
                      onSiteFilter={itemCardActions.onSiteFilter}
                    />
                    {authors.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', pl: '8px', flexShrink: 0 }}>
                        {authors.map((a) => (
                          <AuthorAvatar
                            key={a.id}
                            author={a}
                            name={getAuthorName(a)}
                            size={32}
                            onAuthorView={itemCardActions.onAuthorView}
                            onAuthorFilter={itemCardActions.onAuthorFilter}
                          />
                        ))}
                      </Box>
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      {namedAuthors.length > 0 && (
                        <Box sx={{
                          fontFamily: SERIF_STACK,
                          fontWeight: 700,
                          fontSize: '.95rem',
                          color: TEXT_DARK,
                          lineHeight: 1.2,
                          overflowWrap: 'anywhere',
                        }}>
                          {namedAuthors.map((a, i) => (
                            <React.Fragment key={a.id || getAuthorName(a)}>
                              {i > 0 && (i === namedAuthors.length - 1 ? ' & ' : ', ')}
                              <BylineAuthorName
                                author={a}
                                name={getAuthorName(a)}
                                onAuthorView={itemCardActions.onAuthorView}
                                onAuthorFilter={itemCardActions.onAuthorFilter}
                              />
                            </React.Fragment>
                          ))}
                        </Box>
                      )}
                      <Box sx={{ fontSize: '.78rem', color: TEXT_SECONDARY, mt: '2px' }}>
                        <BylineSiteName
                          site={primarySite}
                          siteId={primarySiteId}
                          domain={domain}
                          onSiteView={itemCardActions.onSiteView}
                          onSiteFilter={itemCardActions.onSiteFilter}
                        />
                        {bylineDate ? ` · ${bylineDate}` : ''}
                      </Box>
                    </Box>
                  </Box>
                )}

                {editing && (
                  <ItemHeroEditForm
                    namespace={namespace}
                    draft={draft}
                    setField={setField}
                    toggleDateOnly={toggleDateOnly}
                    addAuthorEntry={addAuthorEntry}
                    setAuthorName={setAuthorName}
                    removeAuthor={removeAuthor}
                    moveAuthor={moveAuthor}
                    addUrl={addUrl}
                    setUrl={setUrl}
                    removeUrl={removeUrl}
                    makeUrlPrimary={makeUrlPrimary}
                    toggleUrlVisible={toggleUrlVisible}
                    suggesting={suggesting}
                    candidatesFor={candidatesFor}
                    agreesOn={agreesOn}
                    applied={applied}
                    applyCandidate={applyCandidate}
                    restoreField={restoreField}
                    authorCandidates={suggestions?.authors || []}
                    toggleAuthorCandidate={toggleAuthorCandidate}
                  />
                )}
              </Box>

              {!editing && (
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0,
                }}>
                  {readTimeMinutes ? <Pill>{`${readTimeMinutes} min`}</Pill> : null}
                  {item.lang && (
                    <Pill>
                      <PublicIcon sx={{ fontSize: 11, color: MUTED }} />
                      {languageName(item.lang)}
                    </Pill>
                  )}
                  <Box sx={{
                    background: typeColor(item.type || 0),
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '3px 8px',
                    textTransform: 'lowercase',
                    lineHeight: 1,
                    letterSpacing: '.3px',
                    whiteSpace: 'nowrap',
                  }}>
                    {typeLabel(item.type || 0)}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Title */}
          {!editing && (
            <Box component="h1" sx={{
              fontFamily: SERIF_STACK,
              fontWeight: 700,
              fontSize: '2.3rem',
              lineHeight: 1.2,
              color: TEXT_DARK,
              m: 0,
            }}>
              {item.title}
            </Box>
          )}
          {editing && (
            <>
              <GroupLabel>
                Title
                {agreesOn('title') && <SuggestionAgrees />}
              </GroupLabel>
              <TextArea
                value={draft.title}
                onChange={(e) => setField('title', e.target.value)}
                rows={2}
                style={{
                  width: '100%',
                  fontFamily: SERIF_STACK,
                  fontWeight: 700,
                  fontSize: EDIT_FONT_SIZE,
                  lineHeight: 1.25,
                  color: TEXT_DARK,
                  padding: '10px 14px',
                }}
              />
              {suggesting && <SuggestionSkeleton count={2} />}
              <SuggestionPanel
                candidates={candidatesFor('title')}
                appliedId={applied.title}
                onPick={(c) => applyCandidate('title', c)}
                onRestore={() => restoreField('title')}
                renderValue={(c) => (
                  <Box sx={{
                    fontFamily: SERIF_STACK,
                    fontWeight: 700,
                    fontSize: '.95rem',
                    lineHeight: 1.4,
                    color: TEXT_DARK,
                  }}>
                    {c.value}
                  </Box>
                )}
                sx={{ mt: '10px' }}
              />
            </>
          )}

          <Box sx={{ height: 20 }} />

          {/* Excerpt */}
          {!editing && item.excerpt && (
            <Box component="p" sx={{
              fontFamily: SERIF_STACK,
              fontWeight: 400,
              fontSize: '1.1rem',
              lineHeight: 1.75,
              color: TEXT_BODY,
              m: 0,
              textWrap: 'pretty',
              whiteSpace: 'pre-wrap',
            }}>
              {item.excerpt}
            </Box>
          )}
          {editing && (
            <>
              <GroupLabel>
                Excerpt
                {agreesOn('excerpt') && <SuggestionAgrees />}
              </GroupLabel>
              <TextArea
                value={draft.excerpt}
                onChange={(e) => setField('excerpt', e.target.value)}
                rows={7}
                style={{
                  width: '100%',
                  fontFamily: SERIF_STACK,
                  fontWeight: 400,
                  fontSize: EDIT_FONT_SIZE,
                  lineHeight: 1.7,
                  color: TEXT_BODY,
                  padding: '12px 14px',
                }}
              />
              {suggesting && <SuggestionSkeleton height={52} count={2} />}
              <SuggestionPanel
                candidates={candidatesFor('excerpt')}
                appliedId={applied.excerpt}
                onPick={(c) => applyCandidate('excerpt', c)}
                onRestore={() => restoreField('excerpt')}
                renderValue={(c) => (
                  <Box sx={{
                    fontFamily: SERIF_STACK,
                    fontWeight: 400,
                    fontSize: '.9rem',
                    lineHeight: 1.6,
                    color: TEXT_BODY,
                  }}>
                    {c.value}
                  </Box>
                )}
                sx={{ mt: '10px' }}
              />
            </>
          )}

          {/* Primary CTA */}
          {!editing && primaryUrl && (
            <Box
              component="a"
              href={primaryUrl}
              target="_blank"
              rel="noopener"
              sx={{
                mt: '26px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '14px 24px',
                background: coral,
                color: '#fff',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '1rem',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                boxShadow: `0 3px 12px ${alpha(coral, 0.32)}`,
                '&:hover': { background: coralDark, boxShadow: `0 5px 18px ${alpha(coral, 0.42)}` },
              }}
            >
              <span>{`${ctaVerb(item.type || 0)} at ${domain}`}</span>
              <OpenInNewIcon sx={{ fontSize: 17 }} />
            </Box>
          )}

          {/* Additional source URLs */}
          {!editing && secondaryLinks.length > 0 && (
            <Box sx={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 16px', mt: '16px',
            }}>
              <Box sx={{
                fontSize: '.72rem',
                fontWeight: 700,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: MUTED,
              }}>
                Also at
              </Box>
              {secondaryLinks.map(({ url, domain: linkDomain, icon }) => (
                <Box
                  key={url}
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener"
                  title={linkDomain}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: TEXT_SECONDARY,
                    fontSize: '.85rem',
                    textDecoration: 'none',
                    '&:hover': { color: coral, textDecoration: 'underline' },
                  }}
                >
                  <FallbackImg
                    key={icon}
                    src={icon}
                    sx={{
                      width: 16, height: 16, borderRadius: '3px', objectFit: 'contain', flexShrink: 0,
                    }}
                  />
                  {linkDomain}
                </Box>
              ))}
            </Box>
          )}

          {/* Tags */}
          <Box sx={{
            display: 'flex', flexWrap: 'wrap', gap: '8px', mt: '24px', alignItems: 'center',
          }}>
            {(editing ? draft.tags : (item.tags || [])).map((tag) => (
              <TagChip
                key={tag.id || tag.name}
                onClick={!editing && tag.id ? () => itemCardActions.onTagOpen(tag.id) : undefined}
                sx={{ cursor: !editing && tag.id ? 'pointer' : 'default' }}
              >
                {tag.name}
                {editing && (
                  <ChipRemove type="button" onClick={() => removeTag(tag.name)}>×</ChipRemove>
                )}
              </TagChip>
            ))}
            {editing && (
              <Autocomplete
                freeSolo={!isPending}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                size="small"
                options={tagOptions}
                getOptionLabel={optionLabel}
                filterOptions={isPending ? undefined : withCreateOption}
                value={null}
                inputValue={newTag}
                onInputChange={(e, v) => setNewTag(v)}
                onChange={(e, v) => addTagValue(v)}
                renderOption={renderTagOption}
                slotProps={tagPopperSlotProps}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={isPending ? 'add public tag' : 'add tag'}
                    sx={autocompleteSx}
                  />
                )}
                sx={{ width: 200 }}
              />
            )}
          </Box>

          {/* Your tracking */}
          {showTracking && (
            <Box sx={{ mt: '26px', pt: '18px', borderTop: `1px solid ${HAIRLINE}` }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap',
              }}>
                <FavoriteButton
                  isFavorite={Boolean(item.isFavorite)}
                  onToggle={toggleFavorite}
                />
                {!item.hasPrivateData && (
                  <IconToggle type="button" onClick={toggleBookmark} title="Save to library">
                    <BookmarkBorderIcon sx={{ fontSize: 20, color: '#3c3c3c' }} />
                  </IconToggle>
                )}
                {item.hasPrivateData && !isHidden && (
                  <ShareButton
                    hasPublicData={hasPublicData}
                    changes={shareChanges}
                    title={shareTitle}
                    onShare={share}
                  />
                )}
                {item.hasPrivateData && (
                  isHidden ? (
                    <TrackingChip
                      color={ERROR}
                      title="Hidden — click to unhide"
                      onClick={toggleHidden}
                      icon={<VisibilityOffIcon sx={{ fontSize: 18 }} />}
                      label="Hidden"
                    />
                  ) : (
                    <IconToggle type="button" onClick={toggleHidden} title="Hide">
                      <VisibilityOffIcon sx={{ fontSize: 20, color: '#3c3c3c' }} />
                    </IconToggle>
                  )
                )}
                {!editing && item.hasPrivateData && (
                  isReviewed ? (
                    <TrackingChip
                      color={SUCCESS}
                      title="Reviewed — click to clear"
                      onClick={toggleReviewed}
                      icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                      label={formatReviewed(item.timeReviewed)}
                    />
                  ) : (
                    <IconToggle type="button" onClick={toggleReviewed} title="Mark reviewed">
                      <CheckCircleOutlineIcon sx={{ fontSize: 20, color: '#3c3c3c' }} />
                    </IconToggle>
                  )
                )}
                {editing && (
                  <>
                    <Box sx={{
                      width: '1px', alignSelf: 'stretch', minHeight: 24, background: '#eaeaea', mx: '10px',
                    }} />
                    <Box sx={{
                      fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, mr: '2px',
                    }}>
                      Reviewed
                    </Box>
                    <TextInput
                      type="datetime-local"
                      value={draft.dateReviewed}
                      onChange={(e) => setField('dateReviewed', e.target.value)}
                      style={{ padding: '6px 10px' }}
                    />
                  </>
                )}
              </Box>
            </Box>
          )}

          {/* Related */}
          {showRelated && (
            <Box sx={{ mt: '22px', pt: '18px', borderTop: `1px solid ${HAIRLINE}` }}>
              <SectionLabel>Related</SectionLabel>
              {relatedCards.length > 0 && (
                <Box sx={cardGridSx}>
                  {relatedCards.map((ri) => (
                    <Box key={ri.id} sx={cardCellSx}>
                      <Box sx={cardInnerSx}>
                        <ItemCard item={ri} isPreview namespace={namespace} {...itemCardActions} />
                      </Box>
                      {editEntities && (
                        <CardDeleteButton type="button" onClick={() => removeRelated(ri.id)}>×</CardDeleteButton>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
              {editEntities && (
                <TextInput
                  value={newRelated}
                  onChange={(e) => setNewRelated(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRelated(); } }}
                  placeholder="Add related item by ID, press Enter"
                  style={{ marginTop: 14, width: '100%', maxWidth: 320 }}
                />
              )}
            </Box>
          )}

          {/* In lists */}
          {showLists && (
            <Box sx={{ mt: '22px', pt: '18px', borderTop: `1px solid ${HAIRLINE}` }}>
              <SectionLabel>In lists</SectionLabel>
              {listInfos && listInfos.length > 0 && (
                <Box sx={cardGridSx}>
                  {listInfos.map((listInfo) => {
                    const hasCover = (mergePublicAndPrivateItems(listInfo) || listInfo).coverImageUrl === draftCoverUrl;
                    return (
                      <Box key={listInfo.id} sx={cardCellSx}>
                        <Box sx={cardInnerSx}>
                          <ListInfoCard listInfo={listInfo} onListOpen={onListOpen} />
                        </Box>
                        {editEntities && draftCoverUrl && (
                          <CardCornerButton
                            type="button"
                            onClick={() => setListCover(listInfo.id)}
                            disabled={hasCover}
                            title={hasCover ? 'The list already uses this cover' : 'Use this cover for the list'}
                          >
                            {hasCover
                              ? <CheckIcon sx={{ fontSize: 14 }} />
                              : <ImageOutlinedIcon sx={{ fontSize: 14 }} />}
                          </CardCornerButton>
                        )}
                        {editEntities && (
                          <CardDeleteButton type="button" onClick={() => removeListMembership(listInfo.id)}>×</CardDeleteButton>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
              {editEntities && (
                <OutlinedButton type="button" onClick={() => setListDialogOpen(true)} style={{ marginTop: 14 }}>
                  <AddIcon sx={{ fontSize: 16 }} />
                  Add to list
                </OutlinedButton>
              )}
            </Box>
          )}

          {/* Source data */}
          {showSourceData && (
            <ItemSourceData
              item={item}
              extras={sourceData.extras}
              status={sourceData.status}
              error={sourceData.error}
              onLoad={sourceData.load}
              onRefresh={sourceData.refresh}
              onAddPost={sourceData.addPost}
              onUpdatePost={sourceData.updatePost}
              onRemovePost={sourceData.removePost}
              onRefreshPosts={sourceData.refreshPosts}
              onRefreshScores={onRefetch}
            />
          )}
        </Box>
      </HeroCard>

      {listDialogOpen && (
        <AddItemToListDialog
          open={listDialogOpen}
          item={rawItem}
          onClose={() => setListDialogOpen(false)}
          onMembershipChange={() => onRefetch && onRefetch()}
        />
      )}

      {confirmDialog}
    </PageBackground>
  );
}

// A site icon that removes itself when the image fails to load, along with any
// chrome the caller supplies via `wrapperSx`. The failure is React state rather
// than an inline display:none on the node, because `sx` compiles to a class and
// leaves the style attribute unmanaged — an imperatively hidden node stays
// hidden when the item is re-fetched in place and a working icon arrives.
// Callers pass key={src} so a new source starts from a clean slate.
function FallbackImg({
  src, sx, wrapperSx, onClick, triggerProps,
}) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) return null;

  const img = (
    <Box component="img" src={src} alt="" onError={() => setBroken(true)} sx={sx} />
  );

  if (!wrapperSx) return img;
  return <Box {...triggerProps} onClick={onClick} sx={wrapperSx}>{img}</Box>;
}

FallbackImg.propTypes = {
  src: PropTypes.string,
  sx: PropTypes.object,
  wrapperSx: PropTypes.object,
  onClick: PropTypes.func,
  triggerProps: PropTypes.object,
};

FallbackImg.defaultProps = {
  src: null,
  sx: null,
  wrapperSx: null,
  onClick: undefined,
  triggerProps: null,
};

// An author name in the byline; shows the same hover card as the avatars.
function BylineAuthorName({
  author, name, onAuthorView, onAuthorFilter,
}) {
  const authorId = author ? author.id : null;
  const canOpen = Boolean(onAuthorView && authorId != null);
  const onCardOpen = canOpen ? () => onAuthorView(authorId) : undefined;
  const onCardFilter = authorId != null && onAuthorFilter
    ? () => onAuthorFilter(authorId)
    : undefined;
  const { triggerProps, popper, close } = useAuthorHoverCard(author, {
    onCardOpen,
    onCardFilter,
    placement: 'bottom-start',
  });
  const handleClick = canOpen
    ? (e) => { e.stopPropagation(); close(); onAuthorView(authorId); }
    : undefined;

  return (
    <>
      <Box
        component="span"
        {...triggerProps}
        onClick={handleClick}
        sx={{ cursor: canOpen ? 'pointer' : 'default' }}
      >
        {name}
      </Box>
      {popper}
    </>
  );
}

BylineAuthorName.propTypes = {
  author: PropTypes.object,
  name: PropTypes.string,
  onAuthorView: PropTypes.func,
  onAuthorFilter: PropTypes.func,
};

BylineAuthorName.defaultProps = {
  author: null,
  name: '',
  onAuthorView: null,
  onAuthorFilter: null,
};

function HeroSiteIcon({
  src, site, siteId, onSiteView, onSiteFilter,
}) {
  const canOpen = Boolean(onSiteView && siteId != null);
  const onCardOpen = canOpen ? () => onSiteView(siteId) : undefined;
  const onCardFilter = siteId != null && onSiteFilter
    ? () => onSiteFilter(siteId)
    : undefined;
  const { triggerProps, popper, close } = useSiteHoverCard(site, {
    onCardOpen,
    onCardFilter,
    placement: 'bottom-start',
  });
  const handleClick = canOpen
    ? (e) => { e.stopPropagation(); close(); onSiteView(siteId); }
    : undefined;

  return (
    <>
      <FallbackImg
        key={src}
        src={src}
        triggerProps={triggerProps}
        onClick={handleClick}
        wrapperSx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          background: '#fff',
          border: `1px solid ${HAIRLINE}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          flexShrink: 0,
          cursor: canOpen ? 'pointer' : 'default',
        }}
        sx={{ width: 22, height: 22, objectFit: 'contain' }}
      />
      {popper}
    </>
  );
}

HeroSiteIcon.propTypes = {
  src: PropTypes.string,
  site: PropTypes.object,
  siteId: PropTypes.string,
  onSiteView: PropTypes.func,
  onSiteFilter: PropTypes.func,
};

HeroSiteIcon.defaultProps = {
  src: null,
  site: null,
  siteId: null,
  onSiteView: null,
  onSiteFilter: null,
};

// The domain in the byline; the site counterpart of BylineAuthorName.
function BylineSiteName({
  site, siteId, domain, onSiteView, onSiteFilter,
}) {
  const canOpen = Boolean(onSiteView && siteId != null);
  const onCardOpen = canOpen ? () => onSiteView(siteId) : undefined;
  const onCardFilter = siteId != null && onSiteFilter
    ? () => onSiteFilter(siteId)
    : undefined;
  const { triggerProps, popper, close } = useSiteHoverCard(site, {
    onCardOpen,
    onCardFilter,
    placement: 'bottom-start',
  });
  const handleClick = canOpen
    ? (e) => { e.stopPropagation(); close(); onSiteView(siteId); }
    : undefined;

  return (
    <>
      <Box
        component="span"
        {...triggerProps}
        onClick={handleClick}
        sx={{ cursor: canOpen ? 'pointer' : 'default' }}
      >
        {domain}
      </Box>
      {popper}
    </>
  );
}

BylineSiteName.propTypes = {
  site: PropTypes.object,
  siteId: PropTypes.string,
  domain: PropTypes.string,
  onSiteView: PropTypes.func,
  onSiteFilter: PropTypes.func,
};

BylineSiteName.defaultProps = {
  site: null,
  siteId: null,
  domain: '',
  onSiteView: null,
  onSiteFilter: null,
};

// An active tracking state, shown as a bordered chip-button (icon + label) in
// the state's color; clicking it toggles the state back off. The bare icon is
// used for the off state.
function TrackingChip({
  color, icon, label, title, onClick,
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      title={title}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        border: `1px solid ${color}`,
        borderRadius: '6px',
        background: '#fff',
        color,
        cursor: 'pointer',
        fontFamily: SANS_STACK,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1,
        padding: '6px 9px',
        whiteSpace: 'nowrap',
        '&:hover': { background: '#fafafa' },
      }}
    >
      {icon}
      {label}
    </Box>
  );
}

TrackingChip.propTypes = {
  color: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  label: PropTypes.node.isRequired,
  title: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};

TrackingChip.defaultProps = {
  title: undefined,
};

ItemHeroPage.propTypes = {
  item: PropTypes.object.isRequired,
  listInfos: PropTypes.array,
  namespace: PropTypes.string,
  onRefetch: PropTypes.func,
};

ItemHeroPage.defaultProps = {
  listInfos: [],
  namespace: 'public',
  onRefetch: null,
};
